import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import { Settings } from "../../../settings";

// Configuration constants (export for easy tweaking)
export const WIN_LINES_CONFIG = {
  widthMultiplier: 0.3,
  heightMultiplier: 0.326,
  xOffsetMultiplier: -0.18,
  yOffsetMultiplier: -0.9,
};

// Threshold: when active lines exceed this, skip-frame updates kick in
const SKIP_FRAME_THRESHOLD = 2;

/**
 * WinLinesController - Manages win line spine animations
 *
 * Architecture: Pre-warmed spine-per-line with skip-frame updates
 * - One spine instance per win line, all created and warmed at initialization
 * - Line count dynamically extracted from spine data (works for any game)
 * - autoUpdate disabled on all spines — manual update via shared ticker
 * - When 3+ lines active: updates every 2nd frame to halve bone processing cost
 * - Active-only tracking: show/hide/update only touches relevant spines
 */
class WinLinesController {
  constructor(container, symbolWidth, symbolHeight, dividerDimensions) {
    this.container = container;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.dividerDimensions = dividerDimensions;

    // One spine instance per win line, keyed by line ID
    this.winLineSpines = {};
    this.spineContainer = new PIXI.Container();
    this.currentLineId = null;
    this.initialized = false;

    // Dynamically discovered from spine data
    this.winLineCount = 0;
    this.availableAnimations = [];

    // Track currently active line IDs (avoids iterating all spines)
    this.activeLineIds = [];

    // Skip-frame update state
    this._frameCounter = 0;
    this._tickerBound = this._onTick.bind(this);
    this._tickerActive = false;

    // Internal caches for performance optimization
    this._scaleAndPositionCache = null;
    this._lastSymbolWidth = symbolWidth;
    this._lastSymbolHeight = symbolHeight;

    // Add spine container to main container
    this.container.addChild(this.spineContainer);

    // Initialize all spines synchronously (same pattern as OverlayReelController)
    this.initializeSpines();
  }

  /**
   * Initialize spine instances — one per win line, all pre-warmed
   * Dynamically discovers win_line_* animation count from spine data
   */
  initializeSpines() {
    if (!this.symbolWidth || !this.symbolHeight) {
      return;
    }

    // Create a probe spine to discover available win_line_* animations
    const probeSpine = this.createWinLineSpine();
    if (!probeSpine || !probeSpine.skeleton || !probeSpine.state) {
      return;
    }

    // Extract all win_line_* animation names dynamically
    const allAnimations = this.getAnimationNames(probeSpine);
    this.availableAnimations = allAnimations.filter((name) =>
      name.startsWith("win_line_")
    );
    this.winLineCount = this.availableAnimations.length;

    if (this.winLineCount === 0) {
      probeSpine.destroy({ children: true, texture: false, baseTexture: false });
      return;
    }

    // Use probe spine as the first line's instance
    this.warmAndStoreSpine(probeSpine, this.availableAnimations[0], 0);

    // Create and warm remaining spines — one per win line
    for (let i = 1; i < this.winLineCount; i++) {
      const spine = this.createWinLineSpine();
      if (!spine || !spine.state) continue;

      this.warmAndStoreSpine(spine, this.availableAnimations[i], i);
    }

    // Scale and position the entire spine container
    this.scaleAndPositionSpineContainer();

    // Start with container hidden (GPU still processes at 0.001)
    this.spineContainer.alpha = 0.001;

    this.initialized = true;
  }

  /**
   * Warm up a spine instance and store it in the pool
   * Sets animation, triggers GPU compilation, then pauses at frame 0
   * @param {Spine} spine - The spine instance to warm
   * @param {string} animationName - The win_line_* animation name
   * @param {number} lineIndex - Index into availableAnimations
   */
  warmAndStoreSpine(spine, animationName, lineIndex) {
    // autoUpdate OFF — we control updates manually via ticker
    spine.autoUpdate = false;
    this.spineContainer.addChild(spine);

    // GPU invisible but still in render pipeline
    spine.alpha = 0.001;

    // Set animation → triggers shader compilation and mesh creation
    spine.state.setAnimation(0, animationName, true);
    spine.state.update(0.016);

    // Populate bone/slot hierarchy for GPU
    try {
      spine.skeleton.updateWorldTransform();
    } catch (e) {
      // Physics not initialized on some spines — safe to continue
    }

    // Pause at frame 0 — GPU has already compiled everything
    spine.state.timeScale = 0;

    // Extract the numeric line ID from animation name (e.g., "win_line_3" → 3)
    const lineId = parseInt(animationName.replace("win_line_", ""), 10);
    this.winLineSpines[lineId] = spine;
  }

  /**
   * Get animation names from a spine instance
   */
  getAnimationNames(spine) {
    if (!spine?.skeleton?.data?.animations) return [];
    return spine.skeleton.data.animations.map((anim) => anim.name);
  }

  /**
   * Create a single win lines spine instance
   */
  createWinLineSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "win_lines-skeleton";
      const atlasAlias = "win_lines-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!spine || !spine.skeleton || !spine.state) {
        return null;
      }

      if (!spine.skeleton.data || !spine.skeleton.data.animations) {
        return null;
      }

      return spine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Shared ticker callback — updates only active spines manually
   * When 1-2 lines: updates every frame (smooth)
   * When 3+ lines: updates every 2nd frame (halves bone cost)
   */
  _onTick() {
    const activeCount = this.activeLineIds.length;
    if (activeCount === 0) return;

    this._frameCounter++;

    // Skip every other frame when many lines are active
    if (activeCount > SKIP_FRAME_THRESHOLD && this._frameCounter % 2 !== 0) {
      return;
    }

    // Compute delta time (in seconds) — use PIXI ticker's deltaMS
    const dt = PIXI.Ticker.shared.deltaMS / 1000;
    // When skipping frames, compensate so animation speed stays correct
    const compensatedDt = activeCount > SKIP_FRAME_THRESHOLD ? dt * 2 : dt;

    for (let i = 0; i < activeCount; i++) {
      const lineId = this.activeLineIds[i];
      const spine = this.winLineSpines[lineId];
      if (spine && spine.state && spine.state.timeScale > 0) {
        spine.update(compensatedDt);
      }
    }
  }

  /**
   * Start the manual update ticker
   */
  _startTicker() {
    if (!this._tickerActive) {
      this._frameCounter = 0;
      PIXI.Ticker.shared.add(this._tickerBound);
      this._tickerActive = true;
    }
  }

  /**
   * Stop the manual update ticker
   */
  _stopTicker() {
    if (this._tickerActive) {
      PIXI.Ticker.shared.remove(this._tickerBound);
      this._tickerActive = false;
      this._frameCounter = 0;
    }
  }

  /**
   * Scale and position the spine container
   * Cached for performance — invalidated when dimensions change
   */
  scaleAndPositionSpineContainer() {
    if (!this.spineContainer || !this.symbolWidth || !this.symbolHeight) {
      return;
    }

    // Check cache first
    if (
      this._scaleAndPositionCache &&
      this._lastSymbolWidth === this.symbolWidth &&
      this._lastSymbolHeight === this.symbolHeight
    ) {
      const cached = this._scaleAndPositionCache;
      this.spineContainer.scale.set(cached.scale, cached.scale);
      this.spineContainer.position.set(cached.x, cached.y);
      return;
    }

    const nativeSpineWidth = Settings.SYMBOL_WIDTH;
    const nativeSpineHeight = Settings.SYMBOL_HEIGHT;

    const cfg = WIN_LINES_CONFIG;
    const targetFrameWidth =
      this.symbolWidth * Settings.REEL_COUNT * cfg.widthMultiplier;
    const targetFrameHeight =
      this.symbolHeight * Settings.SYMBOLS_VISIBLE * cfg.heightMultiplier;

    const scaleX = targetFrameWidth / nativeSpineWidth;
    const scaleY = targetFrameHeight / nativeSpineHeight;
    const scale = Math.min(scaleX, scaleY);

    const scaledWidth = nativeSpineWidth * scale;
    const scaledHeight = nativeSpineHeight * scale;

    const centerX = (targetFrameWidth - scaledWidth) / 2;
    const centerY = (targetFrameHeight - scaledHeight) / 2;

    const xOffset = targetFrameWidth * cfg.xOffsetMultiplier;
    const yOffset = targetFrameHeight * cfg.yOffsetMultiplier;

    const x = centerX + xOffset;
    const y = centerY + yOffset;

    this._scaleAndPositionCache = { scale, x, y };
    this._lastSymbolWidth = this.symbolWidth;
    this._lastSymbolHeight = this.symbolHeight;

    this.spineContainer.scale.set(scale, scale);
    this.spineContainer.position.set(x, y);
  }

  /**
   * Show multiple win lines simultaneously
   * Only touches the requested spines — all others remain paused and invisible
   * @param {Array<number|string>} lineIds - Array of line IDs to show
   */
  showMultipleWinLines(lineIds) {
    if (!this.initialized) {
      return;
    }

    if (!Array.isArray(lineIds) || lineIds.length === 0) {
      return;
    }

    // Hide container during setup
    this.spineContainer.alpha = 0.001;

    // Stop ticker while setting up
    this._stopTicker();

    // Pause only previously active spines (not all N)
    for (let i = 0; i < this.activeLineIds.length; i++) {
      const prevLineId = this.activeLineIds[i];
      const spine = this.winLineSpines[prevLineId];
      if (spine && spine.state) {
        spine.alpha = 0.001;
        spine.state.timeScale = 0;
      }
    }

    // Activate the requested line spines
    const validLineIds = [];
    for (let i = 0; i < lineIds.length; i++) {
      const lineId = lineIds[i];
      const spine = this.winLineSpines[lineId];
      if (!spine || !spine.state) continue;

      const animationName = `win_line_${lineId}`;

      // Reset animation to frame 0 for perfect synchronization
      spine.state.clearTracks();
      spine.state.setAnimation(0, animationName, true);
      spine.state.update(0);

      spine.alpha = 1;
      spine.state.timeScale = 1;
      validLineIds.push(lineId);
    }

    this.activeLineIds = validLineIds;
    this.currentLineId = "multiple";

    // Start manual ticker for active spine updates
    this._startTicker();

    // Show container — all requested lines appear at once
    this.spineContainer.alpha = 1;
  }

  /**
   * Show a single win line animation (for cycling through individual lines)
   * @param {number|string} lineId - The win line ID
   */
  showWinLine(lineId) {
    // Clear any pending second-play timeout from previous line
    if (this._winLineSoundTimeout) {
      clearTimeout(this._winLineSoundTimeout);
      this._winLineSoundTimeout = null;
    }

    if (!this.initialized) {
      return;
    }

    if (lineId == null) return;

    const spine = this.winLineSpines[lineId];
    if (!spine || !spine.state) {
      return;
    }

    // Hide container during setup
    this.spineContainer.alpha = 0.001;

    // Stop ticker while setting up
    this._stopTicker();

    // Pause only previously active spines (not all N)
    for (let i = 0; i < this.activeLineIds.length; i++) {
      const prevLineId = this.activeLineIds[i];
      const prevSpine = this.winLineSpines[prevLineId];
      if (prevSpine && prevSpine.state) {
        prevSpine.alpha = 0.001;
        prevSpine.state.timeScale = 0;
      }
    }

    // Reset animation to frame 0
    const animationName = `win_line_${lineId}`;
    spine.state.clearTracks();
    spine.state.setAnimation(0, animationName, true);
    spine.state.update(0);

    spine.alpha = 1;
    spine.state.timeScale = 1;

    this.activeLineIds = [lineId];
    this.currentLineId = lineId;

    // Start manual ticker (1 line = every frame, no skipping)
    this._startTicker();

    // Show container
    this.spineContainer.alpha = 1;
  }

  /**
   * Hide current win line animation(s)
   * Only touches active spines — resets them to clean state
   */
  hideWinLine() {
    // Stop ticker — no active lines to update
    this._stopTicker();

    // Hide container
    this.spineContainer.alpha = 0.001;

    // Pause and reset only the active spines (not all N)
    for (let i = 0; i < this.activeLineIds.length; i++) {
      const lineId = this.activeLineIds[i];
      const spine = this.winLineSpines[lineId];
      if (spine && spine.state) {
        spine.state.timeScale = 0;
        spine.state.clearTracks();
        // Full skeleton reset — cleans up slot attachments
        // so next show starts from a completely clean slate
        if (spine.skeleton?.setToSetupPose) {
          spine.skeleton.setToSetupPose();
        }
        spine.alpha = 0.001;
      }
    }

    this.activeLineIds = [];
    this.currentLineId = null;
  }

  /**
   * Update spine dimensions and rescale for resize events
   */
  updateDimensionsAndRescale(symbolWidth, symbolHeight, dividerDimensions) {
    if (
      this.symbolWidth === symbolWidth &&
      this.symbolHeight === symbolHeight
    ) {
      return;
    }

    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.dividerDimensions = dividerDimensions;

    this._scaleAndPositionCache = null;

    if (this.spineContainer && this.initialized) {
      this.scaleAndPositionSpineContainer();
    }
  }

  /**
   * Handle container resize
   */
  resizeToContainer() {
    if (this.spineContainer && this.initialized) {
      this.scaleAndPositionSpineContainer();
    }
  }

  /**
   * Retry initialization if it failed initially
   */
  retryInitialization() {
    if (this.winLineCount === 0 || Object.keys(this.winLineSpines).length === 0) {
      this.initializeSpines();
    }
  }

  /**
   * Check if controller is ready
   */
  isReady() {
    return this.initialized && Object.keys(this.winLineSpines).length > 0;
  }

  /**
   * Clean up all spine instances and resources
   */
  cleanup() {
    // Stop ticker
    this._stopTicker();

    // Destroy all spine instances
    const lineIds = Object.keys(this.winLineSpines);
    for (let i = 0; i < lineIds.length; i++) {
      const spine = this.winLineSpines[lineIds[i]];
      if (spine) {
        if (spine.state) {
          spine.state.clearTracks();
          spine.state.clearListeners();
        }
        if (spine.parent) {
          spine.parent.removeChild(spine);
        }
        spine.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      }
    }
    this.winLineSpines = {};

    if (this.spineContainer) {
      if (this.spineContainer.parent) {
        this.spineContainer.parent.removeChild(this.spineContainer);
      }
      this.spineContainer.destroy({ children: true });
      this.spineContainer = null;
    }

    this.activeLineIds = [];
    this.currentLineId = null;
    this.winLineCount = 0;
    this.availableAnimations = [];
    this.initialized = false;
  }
}

export default WinLinesController;
